import React from 'react'
import { TaskService } from '@duck/api-definitions'
import { Badge } from '@duck/ui'
import { getDifficultyBadgeColor, formatDifficulty, type Column } from './utils'
import { TasksTableSkeleton } from './TasksTableSkeleton'
import { useTar } from '@/modules'
import { TASK_PROFICIENCIES } from '@/utils/taskProficiencies'

interface TasksTableProps {
  tasks: TaskService.Task[]
  totalTasks: number
  visibleColumns: Column[]
  sortField: keyof TaskService.Task
  sortDirection: 'asc' | 'desc'
  onSort: (field: keyof TaskService.Task) => void
  onTaskClick?: (task: TaskService.Task) => void
  loadingMode?: boolean
}

export const TasksTable: React.FC<TasksTableProps> = ({
  tasks,
  totalTasks,
  visibleColumns,
  sortField,
  sortDirection,
  onSort,
  onTaskClick,
  loadingMode = false,
}) => {
  const { t } = useTar()
  const cellRenderers: Record<keyof TaskService.Task, (task: TaskService.Task) => React.ReactNode> = {
    id: (task) => (
      <span className="text-xs font-mono text-muted-foreground">
        {task.id}
      </span>
    ),
    name: (task) => (
      <p className="text-sm font-medium text-foreground">
        {task.name}
      </p>
    ),
    teaser: (task) => (
      <p className="text-sm text-muted-foreground truncate max-w-md" title={task.teaser}>
        {task.teaser}
      </p>
    ),
    body: (task) => (
      <p className="text-sm text-muted-foreground truncate max-w-md" title={task.body}>
        {task.body}
      </p>
    ),
    difficulty: (task) => (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getDifficultyBadgeColor(task.difficulty)}`}>
        {formatDifficulty(task.difficulty)}
      </span>
    ),
    description: (task) => (
      <p className="text-sm text-muted-foreground truncate max-w-md" title={task.description}>
        {task.description}
      </p>
    ),
    ownerId: (task) => (
      <span className="text-sm text-muted-foreground">
        {task.ownerId || t('common.unassigned')}
      </span>
    ),
    language: (task) => (
      <span className="text-sm text-muted-foreground uppercase">
        {task.language}
      </span>
    ),
    defaultQuery: (task) => (
      <div className="max-w-xs">
        <code className="text-xs text-muted-foreground bg-secondary/5 px-2 py-1 rounded truncate block" title={task.defaultQuery}>
          {task.defaultQuery}
        </code>
      </div>
    ),
    dataQuery: (task) => (
      <div className="max-w-xs">
        <code className="text-xs text-muted-foreground bg-secondary/5 px-2 py-1 rounded truncate block" title={task.dataQuery}>
          {task.dataQuery}
        </code>
      </div>
    ),
    seedFactor: (task) => (
      <span className="text-sm text-muted-foreground">
        {task.seedFactor}
      </span>
    ),
    feedbackPayload: (task) => (
      <div className="max-w-xs">
        {task.feedbackPayload ? (
          <code className="text-xs text-muted-foreground bg-secondary/5 px-2 py-1 rounded truncate block" title={task.feedbackPayload}>
            {task.feedbackPayload}
          </code>
        ) : (
          <span className="text-xs text-muted-foreground italic">{t('common.noPayload')}</span>
        )}
      </div>
    ),
    dataBank: (task) => (
      <div className="max-w-xs">
        {task.dataBank ? (
          <code className="text-xs text-muted-foreground bg-secondary/5 px-2 py-1 rounded truncate block" title={task.dataBank}>
            {task.dataBank}
          </code>
        ) : (
          <span className="text-xs text-muted-foreground italic">{t('common.noDataBank')}</span>
        )}
      </div>
    ),
    accessGroups: (task) => (
      <div className="max-w-xs">
        {task.accessGroups && task.accessGroups.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {task.accessGroups.map((group, index) => (
              <Badge key={index} variant="outline" className="bg-accent/10 text-accent border-accent/20">
                {group}
              </Badge>
            ))}
          </div>
        ) : (
          <span className="text-xs text-muted-foreground italic">{t('common.noGroups')}</span>
        )}
      </div>
    ),
    proficiencies: (task) => (
      <div className="max-w-xs">
        {task.proficiencies && task.proficiencies.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {task.proficiencies.map((proficiency, index) => {
              const proficiencyKey = proficiency as TaskService.TaskProficienciesEnum
              const proficiencyConfig = TASK_PROFICIENCIES[proficiencyKey]

              return (
                <div key={index} className="flex items-center gap-1.5" title={proficiency}>
                  {proficiencyConfig?.icon()}
                  <span className="text-xs text-foreground capitalize">
                    {proficiencyConfig ? t(proficiencyConfig.label) : proficiency}
                  </span>
                </div>
              )
            })}
          </div>
        ) : (
          <span className="text-xs text-muted-foreground italic">{t('common.noProficiencies')}</span>
        )}
      </div>
    ),
    solutionPlaceholder: (task) => (
      <div className="max-w-xs">
        <code className="text-xs text-muted-foreground bg-secondary/5 px-2 py-1 rounded truncate block" title={task.solutionPlaceholder}>
          {task.solutionPlaceholder}
        </code>
      </div>
    ),
    availabilityWeight: (task) => (
      <span className="text-sm text-muted-foreground">
        {task.availabilityWeight}
      </span>
    ),
    deleted: (task) => (
      <span className="text-sm text-muted-foreground">{task.deleted ? t('common.yes') : t('common.no')}</span>
    ),
  }

  const renderCellContent = (task: TaskService.Task, columnKey: keyof TaskService.Task) => {
    const renderer = cellRenderers[columnKey]
    return renderer ? renderer(task) : null
  }

  return (
    <div className="bg-card shadow overflow-hidden rounded-md sm:rounded-md border border-border">
      <div className="px-4 py-5 sm:px-6">
        <h3 className="text-lg leading-6 font-medium text-foreground">
          {t('tasks.allTasks')}
        </h3>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          {loadingMode ? t('tasks.loadingTasks') : t('tasks.tasksShown', { tasksShown: tasks.length, totalTasks: totalTasks })}
        </p>
      </div>

      <div className="overflow-x-auto overflow-y-hidden">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-secondary/5">
            <tr>
              {visibleColumns.map((column) => (
                <th
                  key={column.key}
                  className="px-6 py-3 text-left text-xs font-medium text-muted-foreground tracking-wider"
                >
                  {column.sortable ? (
                    <button
                      onClick={() => onSort(column.key as keyof TaskService.Task)}
                      className="flex items-center space-x-1 hover:text-foreground transition-colors"
                    >
                      <span>{t(column.label)}</span>
                      {sortField === column.key && (
                        <span className="text-accent">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </button>
                  ) : (
                    t(column.label)
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {loadingMode ? (
              <TasksTableSkeleton visibleColumns={visibleColumns} rowCount={8} />
            ) : (
              tasks.map((task) => (
                <tr
                  key={task.id}
                  className={`hover:bg-secondary/5 ${onTaskClick ? 'cursor-pointer' : ''}`}
                  onClick={() => onTaskClick?.(task)}
                >
                  {visibleColumns.map((column) => (
                    <td key={column.key} className="px-6 py-4 whitespace-nowrap">
                      {renderCellContent(task, column.key)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {!loadingMode && tasks.length === 0 && (
        <div className="text-center py-12">
          <p className="text-sm text-muted-foreground">{t('tasks.noTasksMatchFilters')}</p>
        </div>
      )}
    </div>
  )
}
